import { computed, type ComputedRef } from 'vue'

import { AUTH_FAMILIES } from '../models/authFamilies'
import type { WorkspaceIntegrationMap } from '../models/integration.types'
import { INTEGRATION_PROVIDERS } from '../models/providers'
import type { AuthFamily, IntegrationProvider } from '../models/providers.types'
import {
  accountsForChannel,
  connectedProviders,
  exclusiveMember,
  hasChannel,
} from '../service/integrationSelectors'
import { canSeeMetaCheckpoint, isMetaCheckpointActive } from '../service/metaCheckpoint'
import { useIntegrationStore } from '../stores/integrationStore'

import {
  useAccount,
  useRbac,
  useWorkspace,
  type UseRbac,
  type UseWorkspace,
} from '@/features/account'

/**
 * Module 12's public read surface — 05's nav visibility, 08's channel gating, 22's settings tabs.
 *
 * The workspace-scoped half reads through the SELECTED workspace. v3's equivalents took the
 * workspace from a store reached inside a Pinia getter; here the composable is the one place that
 * knows which workspace is current, and everything under it is a pure function of a map.
 */
export interface UseIntegrations {
  /** True after `integrations_connected` resolves ready or explicit-empty for this account. */
  isReady: ComputedRef<boolean>
  /** Account-scoped connected-provider count used by Summary's zero-state resolver. */
  integrationCount: ComputedRef<number>
  hasIntegration: (key: string) => boolean

  /** The selected workspace's linked accounts, keyed by workspace-integration key. `{}` when cold. */
  integrations: ComputedRef<WorkspaceIntegrationMap>
  /** Providers this workspace's package allows connecting (v3 `allowedIntegrations`). */
  allowedIntegrations: ComputedRef<readonly string[]>
  /** Provider id → connectable for this user/account. `null` until hydrated. */
  connectableIntegrations: ComputedRef<Record<string, boolean> | null>
  /** Providers with at least one linked account in the selected workspace. */
  connected: ComputedRef<readonly IntegrationProvider[]>
  /**
   * Account-level OAuth families whose token/scope flags require reauthentication. Meta steps out
   * while a visible checkpoint stands (ORP-26183): its own banner says not to reconnect, so the
   * "connection lost" line would contradict it.
   */
  reauthenticationNeeded: ComputedRef<readonly AuthFamily[]>
  /**
   * Read-only typed provider descriptors. Consumers combine this stable catalog with the reactive
   * workspace methods below; connector mutation/configuration remains private to module 12.
   */
  providers: ComputedRef<readonly IntegrationProvider[]>
  /** Linked accounts for a channel, addressed by alias or canonical key. */
  accountsFor: (channel: string) => ReturnType<typeof accountsForChannel>
  /** Whether a channel has any linked account in the selected workspace. */
  isConnected: (channel: string) => boolean
  /**
   * The connected member of a mutually-exclusive group — v3's `getShopIntegration()` /
   * `getAppIntegration()`, minus their four return types.
   */
  exclusive: (group: 'shop' | 'app') => IntegrationProvider | null
}

export function useIntegrations(): UseIntegrations {
  const store = useIntegrationStore()
  const { accountDetail } = useAccount()

  // 07's workspace surface is resolved LAZILY, once, and only if a workspace-scoped field is
  // actually read. The account-scoped half (`isReady`/`integrationCount`/`hasIntegration`) is what
  // most consumers want — 05's nav visibility reads nothing else — and making them all depend on 07's
  // workspace store just to ask "is this account connected to anything" would be a mocking tax on
  // every one of their tests for a value they never touch.
  let workspace: UseWorkspace | null = null
  const workspaceId = (): number | string | undefined =>
    (workspace ??= useWorkspace()).workspaceId.value

  const integrations = computed(() => store.integrationsFor(workspaceId()))

  // Lazy for the same reason as the workspace surface: only the reauth list reads it.
  let rbac: UseRbac | null = null
  const checkpointShowing = (): boolean =>
    canSeeMetaCheckpoint((rbac ??= useRbac()).isSysAdmin.value) &&
    isMetaCheckpointActive(accountDetail.value as Record<string, unknown> | null)

  return {
    isReady: computed(() => store.isReady),
    integrationCount: computed(() => store.connectedIntegrations.length),
    hasIntegration: (key) => store.hasIntegration(key),

    integrations,
    allowedIntegrations: computed(() => store.allowedIntegrations),
    connectableIntegrations: computed(() => store.connectableIntegrations),
    connected: computed(() => connectedProviders(integrations.value)),
    reauthenticationNeeded: computed(() =>
      AUTH_FAMILIES.filter(
        (family) =>
          family.refreshKey !== undefined &&
          Boolean(accountDetail.value?.[family.refreshKey]) &&
          !(family.field === 'meta' && checkpointShowing()),
      ),
    ),
    providers: computed(() => INTEGRATION_PROVIDERS),
    accountsFor: (channel) => accountsForChannel(integrations.value, channel),
    isConnected: (channel) => hasChannel(integrations.value, channel),
    exclusive: (group) => exclusiveMember(integrations.value, group),
  }
}
